import {
    createStyles,
    Divider,
    Grid,
    Link,
    Paper,
    Theme,
    Typography,
    withStyles,
    WithStyles
} from "@material-ui/core";
import { connect } from "react-redux";
import DebugApi from "../../api/DebugApi";
import Constants from "../../models/utility/Constants";
import SecurityProvider from "../../providers/SecurityProvider";
import { IAppState } from "../../store";
import FooterImage from "./../../assets/clhbid_logo_white.png";
import * as UserStore from "./../../store/UserStore";

type FooterProps = UserStore.IUserStoreState & WithStyles<typeof styles>;

const WhiteTextTypography = withStyles({
    root: {
        color: "#FFFFFF"
    }
})(Typography) as typeof Typography;

const styles = (theme: Theme) =>
    createStyles({
        copyrightText: { [theme.breakpoints.down("xs")]: { fontSize: 12 } },
        footer: {
            [theme.breakpoints.down("xs")]: {
                paddingLeft: theme.spacing(5),
                paddingRight: theme.spacing(5)
            },
            backgroundColor: "#424242",
            left: 0,
            margin: "0 auto",
            maxWidth: theme.layout.maxWidth,
            paddingLeft: theme.spacing(8),
            paddingRight: theme.spacing(8),
            width: "100%"
        },
        footerContent: {
            margin: "0 auto",
            maxWidth: theme.bodyContent.maxWidth,
            width: "100%",
            backgroundColor: "#424242"
        },
        footerHeader: {
            marginBottom: theme.spacing(3),
            color: theme.palette.common.white
        },
        footerImage: {
            display: "block",
            height: 30,
            marginBottom: theme.spacing(3),
            width: "auto"
        },
        footerLink: {
            "& a": {
                display: "block",
                marginBottom: theme.spacing()
            },
            display: "block",
            fontWeight: "bold"
        },
        gridItem: { marginBottom: theme.spacing(3) },
        sectionCopyright: { height: 80 },
        sectionMain: {
            [theme.breakpoints.down("xs")]: {
                paddingBottom: theme.spacing(3),
                paddingTop: theme.spacing(5)
            },
            paddingBottom: theme.spacing(5),
            paddingTop: theme.spacing(8)
        },
        phoneStyle: {
            color: theme.palette.common.white
        }
    });

function Footer({ classes, user }: FooterProps) {
    return (
        <footer data-testid="Footer">
            <Divider />
            <Paper className={classes.footer} elevation={2}>
                <div className={classes.footerContent}>
                    <Grid
                        className={classes.sectionMain}
                        container={true}
                        justifyContent="space-between"
                    >
                        <Grid
                            className={classes.gridItem}
                            item={true}
                            md={3}
                            sm={6}
                            xs={12}
                        >
                            <Link href={Constants.Routes.Home()}>
                                <img
                                    className={classes.footerImage}
                                    src={FooterImage}
                                    width="338"
                                    height="60"
                                    alt="CLHbid.com"
                                />
                            </Link>
                            <Typography
                                variant="h6"
                                className={classes.phoneStyle}
                            >
                                <div>T: 1-866-263-7480</div>
                                <Link
                                    href="mailto:info@clhbid.com"
                                    color="primary"
                                >
                                    info@clhbid.com
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.gridItem}
                            item={true}
                            md={3}
                            sm={6}
                            xs={12}
                        >
                            <Typography
                                className={classes.footerHeader}
                                component="h5"
                                variant="h5"
                            >
                                Why CLH
                            </Typography>
                            <Typography
                                className={classes.footerLink}
                                color="primary"
                                variant="body1"
                            >
                                <Link href="https://www.clhbid.com/why-clhbid/">
                                    Why CLHbid.com?
                                </Link>
                                <Link href="https://www.clhbid.com/why-clhbid/clhbid-vs-private-sale/">
                                    Private Sale
                                </Link>
                                <Link href="https://www.clhbid.com/why-clhbid/clhbid-vs-listing-with-professional-realtors/">
                                    Professional Realtors
                                </Link>
                                <Link href="https://www.clhbid.com/why-clhbid/clhbid-vs-public-auction/">
                                    Public Auction
                                </Link>
                                <Link href="https://www.clhbid.com/why-clhbid/clhbid-vs-private-tender/">
                                    Private Tender
                                </Link>
                                <Link href="https://www.clhbid.com/why-clhbid/clhbid-testimonials/">
                                    Testimonials
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.gridItem}
                            item={true}
                            md={3}
                            sm={6}
                            xs={12}
                        >
                            <Typography
                                className={classes.footerHeader}
                                component="h5"
                                variant="h5"
                            >
                                How It Works
                            </Typography>
                            <Typography
                                className={classes.footerLink}
                                color="primary"
                                variant="body1"
                            >
                                <Link href="https://www.clhbid.com/about-clhbid/frequently-asked-questions/">
                                    Frequently Asked Questions
                                </Link>
                                <Link href="https://www.clhbid.com/how-clhbid-works/register-to-bid-on-the-clhbid-auction-platform/">
                                    Registering to Bid
                                </Link>
                                <Link href="https://www.clhbid.com/how-clhbid-works/bidding-on-properties-on-clhbid/">
                                    Bidding
                                </Link>
                                <Link href="https://www.clhbid.com/how-clhbid-works/closing-the-sale-post-auction/">
                                    Closing
                                </Link>
                                <Link href="https://www.clhbid.com/how-clhbid-works/clhbid-bidding-terms-and-conditions/">
                                    Terms & Conditions
                                </Link>
                            </Typography>
                        </Grid>
                        <Grid
                            className={classes.gridItem}
                            item={true}
                            md={3}
                            sm={6}
                            xs={12}
                        >
                            <Typography
                                className={classes.footerHeader}
                                component="h5"
                                variant="h5"
                            >
                                Account
                            </Typography>
                            <Typography
                                className={classes.footerLink}
                                color="primary"
                                variant="body1"
                            >
                                <Link href="https://www.clhbid.com/contact/">
                                    Contact Us
                                </Link>
                                {SecurityProvider.CanDebug(user) && (
                                    <Link
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            DebugApi.DoSystemLatencyTest();
                                            console.log(
                                                "Started Latency Test."
                                            );
                                        }}
                                    >
                                        Latency Test
                                    </Link>
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid
                        className={classes.sectionCopyright}
                        container={true}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Grid item={true}>
                            <WhiteTextTypography
                                component="span"
                                variant="subtitle2"
                                className={classes.copyrightText}
                            >
                                © Copyright CLHbid.com | All Rights Reserved.
                            </WhiteTextTypography>
                        </Grid>
                    </Grid>
                </div>
            </Paper>
        </footer>
    );
}

const reduxState = (state: IAppState) => state.userStoreState;

export default withStyles(styles)(connect(reduxState)(Footer));
